/**
 * The header balances, and where each one is topped up.
 *
 * User: "implement navigation from the game currencies in the navbar to the
 * top-up section for currency and energy."
 *
 * Before this, the four plates were pure readout. A player who saw
 * `0 Energy` in red — which is the one number that stops the Play button —
 * had to already know that Energy is sold on a tab inside a Shop that lives
 * behind a tile in the grid below. Three screens away, and nothing on the
 * plate said so. The same held for Wax (Exchange tab) and for Slots (the
 * Expand button inside the Stash).
 *
 * So every plate is now a button that goes to the place that raises it. This
 * is the standard "tap the balance to top up" affordance, and it is worth
 * having because the balance is exactly where the player is looking at the
 * moment they discover they do not have enough.
 *
 * ## Why the destinations live in a table
 *
 * The bootstrap knows how to open a panel and how to select a shop tab; it
 * should not also be the place that decides Wax is topped up by exchanging
 * Ember. That is an economy fact (see design/gdd/economy-currency.md Core
 * Rules #7 and #8), it changes when the economy changes, and keeping it in
 * one exported table means the tooltip, the accessible name and the click
 * target can never drift from each other.
 */

import { currencyIcon } from './currency';
import { esc } from './item-icon';
import type { ShopTabId } from './shop';
import { t, N_ } from '../i18n/i18n';

/** The four things the header counts. Not `CurrencyKind`: XP has no plate. */
export type BalanceKind = 'wax' | 'ember' | 'slots' | 'energy';

export interface TopUpTarget {
  /** Which hub panel opens. */
  panel: 'shop' | 'inventory';
  /** Which tab inside the Shop, when the panel is the Shop. */
  shopTab?: ShopTabId;
  /**
   * Written as the action the button performs, not as a label. It is both the
   * tooltip and the tail of the accessible name, so "Get more Energy" reads
   * correctly after "3 Energy" and a noun phrase would not.
   */
  action: string;
}

/**
 * Where each balance is raised.
 *
 * Wax points at its own shelf, which sells Wax for Ember at a fixed rate and
 * a daily cap. That is the only way to add Wax without playing (Core Rule
 * #7) — nothing takes real money for it directly.
 *
 * Slots points at the Inventory and not at the Shop, because capacity is
 * bought with the Expand button that sits beside the meter it moves — and
 * since 2026-09-07 that meter is in the Inventory, the Stash panel it used to
 * live in having been removed as a duplicate of the Inventory's own grid.
 */
export const TOP_UP_TARGETS: Record<BalanceKind, TopUpTarget> = {
  wax: { panel: 'shop', shopTab: 'wax', action: N_('Get more Wax') },
  ember: { panel: 'shop', shopTab: 'ember', action: N_('Get more Ember') },
  slots: { panel: 'inventory', action: N_('Expand your stash') },
  energy: { panel: 'shop', shopTab: 'energy', action: N_('Get more Energy') },
};

export interface BalancePlate {
  kind: BalanceKind;
  /** Already formatted — `4600`, or `8/25` for a capacity. */
  value: string;
  label: string;
  /** Tailwind colour class for the numeral. */
  tone: string;
  /** Trailing aside, e.g. the energy refill phrase. */
  note?: string;
}

/**
 * The "add more" mark, at the trailing edge of every plate.
 *
 * Without it the only cue that a plate is pressable is the hover border, and
 * CLAUDE.md's mobile-first rule is explicit that hover may enhance but never
 * carry. On a phone — where most of these players are — a readout that is
 * secretly a button is a readout nobody presses.
 *
 * A plus rather than a chevron: a chevron says "there is more of this list",
 * a plus says "add more of this", which is what the button actually does.
 * Drawn small and in the dim gold so the numeral stays the thing being read.
 */
function addMark(): string {
  return `<svg viewBox="0 0 16 16" width="0.85em" height="0.85em" aria-hidden="true" focusable="false"
    class="shrink-0 -mr-1 opacity-70"><circle cx="8" cy="8" r="6.6" fill="none" stroke="currentColor" stroke-width="1.4"/>
    <path d="M8 4.9v6.2M4.9 8h6.2" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>`;
}

/**
 * One plate, as a button.
 *
 * `aria-label` carries the whole thing — amount, label, note and the action —
 * because the visible content is four separate spans and a screen reader
 * reading them in sequence gives "4600 Wax" with no hint that this is
 * pressable or where it leads. `title` is the sighted equivalent, and the
 * button is `min-h-11` (44px) so it clears the tap-target floor.
 */
export function balancePlateMarkup(plate: BalancePlate): string {
  const target = TOP_UP_TARGETS[plate.kind];
  const action = t(target.action);
  const spoken = esc([`${plate.value} ${plate.label}`, plate.note, action]
    .filter(Boolean).join('. '));
  return `
    <button type="button" data-top-up="${plate.kind}" title="${esc(action)}" aria-label="${spoken}"
      class="frame-pewter min-h-11 px-3.5 flex items-center gap-1.5 text-left
             hover:border-gold/60 tile-press
             focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold">
      <span class="text-[1.15rem] leading-none flex items-center">${currencyIcon(plate.kind)}</span>
      <span data-count="${plate.kind}" class="font-heading font-semibold tabular-nums ${plate.tone}">${esc(plate.value)}</span>
      <span class="eyebrow eyebrow-dim">${esc(plate.label)}</span>
      ${plate.note ? `<span class="text-[12px] text-text-dim">${esc(plate.note)}</span>` : ''}
      <span class="text-gold-deep flex items-center pl-0.5">${addMark()}</span>
    </button>`;
}

/** The strip, in the order the header reads left to right. */
export function balanceStripMarkup(plates: BalancePlate[]): string {
  return plates.map(balancePlateMarkup).join('');
}
